import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Lock, AlertCircle, CheckCircle, Zap, Building2, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

// Add animations to the global style
const animationStyles = `
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInDown {
    from {
      opacity: 0;
      transform: translateY(-30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-40px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(40px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes rotateIn {
    from {
      opacity: 0;
      transform: rotate(-10deg) scale(0.9);
    }
    to {
      opacity: 1;
      transform: rotate(0deg) scale(1);
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      filter: blur(10px);
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      filter: blur(0px);
      transform: translateY(0);
    }
  }

  @keyframes zoomInCenter {
    from {
      opacity: 0;
      transform: scale(0.5);
      filter: blur(8px);
    }
    to {
      opacity: 1;
      transform: scale(1);
      filter: blur(0px);
    }
  }

  @keyframes shakeIn {
    0% {
      opacity: 0;
      transform: translateX(-10px) translateY(10px);
    }
    50% {
      opacity: 0.5;
      transform: translateX(5px) translateY(-5px);
    }
    100% {
      opacity: 1;
      transform: translateX(0) translateY(0);
    }
  }

  @keyframes flipIn {
    from {
      opacity: 0;
      transform: perspective(400px) rotateY(90deg);
    }
    to {
      opacity: 1;
      transform: perspective(400px) rotateY(0deg);
    }
  }

  @keyframes pulse-glow {
    0%, 100% {
      box-shadow: 0 0 20px rgba(6, 182, 212, 0.5);
    }
    50% {
      box-shadow: 0 0 40px rgba(6, 182, 212, 0.8);
    }
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  @keyframes wiggle {
    0%, 100% {
      transform: rotate(0deg);
    }
    25% {
      transform: rotate(-2deg);
    }
    75% {
      transform: rotate(2deg);
    }
  }

  @keyframes swing {
    0%, 100% {
      transform: rotate(0deg);
      transform-origin: center top;
    }
    50% {
      transform: rotate(5deg);
    }
  }

  @keyframes heartbeat {
    0%, 100% {
      transform: scale(1);
    }
    25% {
      transform: scale(1.15);
    }
    50% {
      transform: scale(1);
    }
  }

  @keyframes morphing {
    0% {
      border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
    }
    50% {
      border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%;
    }
    100% {
      border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
    }
  }

  .animate-slide-in-up {
    animation: slideInUp 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  .animate-slide-in-down {
    animation: slideInDown 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  .animate-slide-in-left {
    animation: slideInLeft 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  .animate-slide-in-right {
    animation: slideInRight 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  .animate-scale-in {
    animation: scaleIn 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  .animate-rotate-in {
    animation: rotateIn 0.8s ease-out forwards;
  }

  .animate-fade-in-up {
    animation: fadeInUp 0.8s ease-out forwards;
  }

  .animate-zoom-in {
    animation: zoomInCenter 0.8s ease-out forwards;
  }

  .animate-shake-in {
    animation: shakeIn 0.6s ease-out forwards;
  }

  .animate-flip-in {
    animation: flipIn 0.8s ease-out forwards;
  }

  .animate-pulse-glow {
    animation: pulse-glow 2s ease-in-out infinite;
  }

  .animate-float {
    animation: float 3s ease-in-out infinite;
  }

  .animate-wiggle {
    animation: wiggle 0.5s ease-in-out infinite;
  }

  .animate-swing {
    animation: swing 2s ease-in-out infinite;
  }

  .animate-heartbeat {
    animation: heartbeat 1.3s ease-in-out infinite;
  }

  .animate-morphing {
    animation: morphing 4s ease-in-out infinite;
  }
`;

const QSM = () => {
  const heroRef = useScrollAnimation({ threshold: 0.2 });
  const threatRef = useScrollAnimation({ threshold: 0.2 });
  const analogyRef = useScrollAnimation({ threshold: 0.2 });
  const riskRef = useScrollAnimation({ threshold: 0.2 });
  const regulationRef = useScrollAnimation({ threshold: 0.2 });
  const expertRef = useScrollAnimation({ threshold: 0.2 });
  const actionRef = useScrollAnimation({ threshold: 0.2 });
  const comingSoonRef = useScrollAnimation({ threshold: 0.2 });

  // Mobile detection for enhanced animations
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="pt-20">
      <style>{animationStyles}</style>
      {/* Hero - The Quantum Awakening */}
      <section className="bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 py-24 lg:py-32 overflow-hidden relative" ref={heroRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 items-center">
            <div>
              {isMobile ? (
                <motion.div
                  className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-cyan-400/20 rounded-full border border-cyan-400/40"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                >
                  <TrendingUp className="text-cyan-300" size={18} />
                  <span className="text-sm font-semibold text-cyan-300">The Quantum Era is Here</span>
                </motion.div>
              ) : (
                <div className={`inline-flex items-center gap-2 mb-6 px-4 py-2 bg-cyan-400/20 rounded-full border border-cyan-400/40 transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100' : 'opacity-0'}`}>
                  <TrendingUp className="text-cyan-300" size={18} />
                  <span className="text-sm font-semibold text-cyan-300">The Quantum Era is Here</span>
                </div>
              )}

              {isMobile ? (
                <motion.h1
                  className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
                >
                  Quantum Computers Are Advancing at Lightning Speed
                </motion.h1>
              ) : (
                <h1 className={`text-5xl md:text-6xl font-bold text-white mb-6 leading-tight transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100 animate-slide-in-up' : 'opacity-0'}`} style={{ transitionDelay: heroRef.isVisible ? '0.1s' : undefined }}>
                  Quantum Computers Are Advancing at Lightning Speed
                </h1>
              )}

              {isMobile ? (
                <motion.p
                  className="text-xl text-gray-200 mb-8 leading-relaxed"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                >
                  They're getting faster, smarter, and more powerful every month. And they're coming for your encryption.
                </motion.p>
              ) : (
                <p className={`text-xl text-gray-200 mb-8 leading-relaxed transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: heroRef.isVisible ? '0.2s' : undefined }}>
                  They're getting faster, smarter, and more powerful every month. And they're coming for your encryption.
                </p>
              )}

              {isMobile ? (
                <motion.div
                  className="flex gap-4"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                >
                  <Link to="/contact" className="px-8 py-3 bg-cyan-500 text-gray-900 rounded-full font-semibold hover:bg-cyan-400 transition-colors">
                    Start Your Assessment
                  </Link>
                </motion.div>
              ) : (
                <div className={`flex gap-4 transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: heroRef.isVisible ? '0.3s' : undefined }}>
                  <Link to="/contact" className="px-8 py-3 bg-cyan-500 text-gray-900 rounded-full font-semibold hover:bg-cyan-400 transition-colors">
                    Start Your Assessment
                  </Link>
                </div>
              )}
            </div>

            {isMobile ? (
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              >
                <div className="absolute inset-0 bg-cyan-600 rounded-3xl rotate-3 opacity-10"></div>
                <img
                  src="/quantum-computer.jpg"
                  alt="Quantum Computer"
                  className="relative rounded-3xl shadow-2xl"
                  style={{
                    boxShadow: '0 0 30px rgba(255, 255, 255, 0.3), 0 0 60px rgba(255, 255, 255, 0.15), 0 25px 50px rgba(0, 0, 0, 0.3)'
                  }}
                />
              </motion.div>
            ) : (
              <div className={`relative transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: heroRef.isVisible ? '0.4s' : undefined }}>
                <div className="absolute inset-0 bg-cyan-600 rounded-3xl rotate-3 opacity-10"></div>
                <img
                  src="/quantum-computer.jpg"
                  alt="Quantum Computer"
                  className="relative rounded-3xl shadow-2xl transform hover:-translate-y-2 transition-transform duration-500 hover:scale-[1.02]"
                  style={{
                    boxShadow: '0 0 30px rgba(255, 255, 255, 0.3), 0 0 60px rgba(255, 255, 255, 0.15), 0 25px 50px rgba(0, 0, 0, 0.3)'
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* The Threat - What's Really Happening */}
      <section className="py-24 bg-white" ref={threatRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            {isMobile ? (
              <>
                <motion.h2
                  className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  The Encryption You Trust Today Will Be Broken Tomorrow
                </motion.h2>
                <motion.p
                  className="text-xl text-gray-700 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  Your current encryption (RSA, ECC) relies on mathematical problems that are "hard" for computers to solve. Quantum computers don't follow the same rules. What takes a supercomputer thousands of years, a quantum computer could crack in hours.
                </motion.p>
              </>
            ) : (
              <>
                <h2 className={`text-5xl md:text-6xl font-bold text-gray-900 mb-6 transition-all duration-1000 ${threatRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: threatRef.isVisible ? '0s' : undefined }}>
                  The Encryption You Trust Today Will Be Broken Tomorrow
                </h2>
                <p className={`text-xl text-gray-700 leading-relaxed transition-all duration-1000 ${threatRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: threatRef.isVisible ? '0.1s' : undefined }}>
                  Your current encryption (RSA, ECC) relies on mathematical problems that are "hard" for computers to solve. Quantum computers don't follow the same rules. What takes a supercomputer thousands of years, a quantum computer could crack in hours.
                </p>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {isMobile ? (
              <>
                <motion.div
                  className="bg-red-50 border-l-4 border-red-500 p-8 rounded-lg"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <AlertCircle className="text-red-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">The "Harvest Now, Decrypt Later" Attack</h3>
                  <p className="text-gray-700">
                    Attackers are already collecting encrypted data today—banking records, trade secrets, government communications. They're storing it, waiting for quantum computers to become powerful enough to decrypt it all at once. Your secrets aren't safe today.
                  </p>
                </motion.div>

                <motion.div
                  className="bg-orange-50 border-l-4 border-orange-500 p-8 rounded-lg"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <TrendingUp className="text-orange-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">The Timeline is Shrinking</h3>
                  <p className="text-gray-700">
                    Experts estimate that quantum computers capable of breaking current encryption could exist by 2029-2030. That's less than 5 years away. And migration takes time—usually 2-3 years for most organizations.
                  </p>
                </motion.div>
              </>
            ) : (
              <>
                <div className={`bg-red-50 border-l-4 border-red-500 p-8 rounded-lg transition-all duration-1000 hover:scale-105 hover:shadow-lg ${threatRef.isVisible ? 'opacity-100 animate-slide-in-up' : 'opacity-0'}`} style={{ transitionDelay: threatRef.isVisible ? '0.2s' : undefined }}>
                  <AlertCircle className="text-red-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">The "Harvest Now, Decrypt Later" Attack</h3>
                  <p className="text-gray-700">
                    Attackers are already collecting encrypted data today—banking records, trade secrets, government communications. They're storing it, waiting for quantum computers to become powerful enough to decrypt it all at once. Your secrets aren't safe today.
                  </p>
                </div>

                <div className={`bg-orange-50 border-l-4 border-orange-500 p-8 rounded-lg transition-all duration-1000 hover:scale-105 hover:shadow-lg ${threatRef.isVisible ? 'opacity-100 animate-slide-in-up' : 'opacity-0'}`} style={{ transitionDelay: threatRef.isVisible ? '0.3s' : undefined }}>
                  <TrendingUp className="text-orange-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">The Timeline is Shrinking</h3>
                  <p className="text-gray-700">
                    Experts estimate that quantum computers capable of breaking current encryption could exist by 2029-2030. That's less than 5 years away. And migration takes time—usually 2-3 years for most organizations.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* The Analogy - Make It Real */}
      <section className="py-24 bg-gradient-to-br from-indigo-50 to-purple-50" ref={analogyRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {isMobile ? (
              <motion.div
                className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border-2 border-purple-200"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                <motion.h2
                  className="text-3xl md:text-4xl font-bold text-gray-900 mb-8"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  Think of It Like This
                </motion.h2>

                <div className="space-y-6">
                  <motion.div
                    className="flex gap-4"
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <div className="flex-shrink-0">
                      <div className="relative w-12 h-12">
                        <svg className="w-full h-full" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <linearGradient id="arrowGradient1m" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" style={{ stopColor: '#A855F7', stopOpacity: 0.4 }} />
                              <stop offset="50%" style={{ stopColor: '#9333EA', stopOpacity: 1 }} />
                              <stop offset="100%" style={{ stopColor: '#7E22CE', stopOpacity: 0.4 }} />
                            </linearGradient>
                          </defs>
                          <polygon points="15,10 35,25 15,40" fill="url(#arrowGradient1m)" />
                          <line x1="10" y1="25" x2="35" y2="25" stroke="url(#arrowGradient1m)" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/30 to-purple-600/30 rounded-lg blur-lg animate-pulse"></div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Traditional Encryption</h3>
                      <p className="text-gray-700">Imagine a vault with a combination lock that takes a normal person 10,000 years to guess all possible combinations. You feel safe.</p>
                    </div>
                  </motion.div>

                  <motion.div
                    className="flex gap-4"
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <div className="flex-shrink-0">
                      <div className="relative w-12 h-12">
                        <svg className="w-full h-full" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <linearGradient id="arrowGradient2m" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" style={{ stopColor: '#A855F7', stopOpacity: 0.4 }} />
                              <stop offset="50%" style={{ stopColor: '#9333EA', stopOpacity: 1 }} />
                              <stop offset="100%" style={{ stopColor: '#7E22CE', stopOpacity: 0.4 }} />
                            </linearGradient>
                          </defs>
                          <polygon points="15,10 35,25 15,40" fill="url(#arrowGradient2m)" />
                          <line x1="10" y1="25" x2="35" y2="25" stroke="url(#arrowGradient2m)" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/30 to-purple-600/30 rounded-lg blur-lg animate-pulse"></div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Along Comes Quantum</h3>
                      <p className="text-gray-700">Then someone invents a superhuman lockpicker that can try all 10,000 years of guesses in seconds. Your vault isn't safe anymore.</p>
                    </div>
                  </motion.div>

                  <motion.div
                    className="flex gap-4"
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <div className="flex-shrink-0">
                      <div className="relative w-12 h-12">
                        <svg className="w-full h-full" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <linearGradient id="arrowGradient3m" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" style={{ stopColor: '#A855F7', stopOpacity: 0.4 }} />
                              <stop offset="50%" style={{ stopColor: '#9333EA', stopOpacity: 1 }} />
                              <stop offset="100%" style={{ stopColor: '#7E22CE', stopOpacity: 0.4 }} />
                            </linearGradient>
                          </defs>
                          <polygon points="15,10 35,25 15,40" fill="url(#arrowGradient3m)" />
                          <line x1="10" y1="25" x2="35" y2="25" stroke="url(#arrowGradient3m)" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/30 to-purple-600/30 rounded-lg blur-lg animate-pulse"></div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Post-Quantum Encryption</h3>
                      <p className="text-gray-700">Post-quantum algorithms use math that's hard even for quantum computers. New, stronger vaults that will stay safe for decades to come.</p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ) : (
              <div className={`bg-white rounded-2xl p-12 shadow-xl border-2 border-purple-200 transition-all duration-1000 ${analogyRef.isVisible ? 'opacity-100 animate-flip-in' : 'opacity-0'}`} style={{ transitionDelay: analogyRef.isVisible ? '0s' : undefined }}>
                <h2 className={`text-4xl font-bold text-gray-900 mb-8 transition-all duration-1000 ${analogyRef.isVisible ? 'opacity-100 animate-slide-in-left' : 'opacity-0'}`} style={{ transitionDelay: analogyRef.isVisible ? '0.1s' : undefined }}>Think of It Like This</h2>

                <div className="space-y-6">
                  <div className={`flex gap-4 transition-all duration-1000 ${analogyRef.isVisible ? 'opacity-100 animate-slide-in-right' : 'opacity-0'}`} style={{ transitionDelay: analogyRef.isVisible ? '0.2s' : undefined }}>
                    <div className="flex-shrink-0">
                      <div className="relative w-12 h-12">
                        <svg className="w-full h-full" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <linearGradient id="arrowGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" style={{ stopColor: '#A855F7', stopOpacity: 0.4 }} />
                              <stop offset="50%" style={{ stopColor: '#9333EA', stopOpacity: 1 }} />
                              <stop offset="100%" style={{ stopColor: '#7E22CE', stopOpacity: 0.4 }} />
                            </linearGradient>
                            <filter id="glow1">
                              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                              <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                              </feMerge>
                            </filter>
                          </defs>
                          <polygon points="15,10 35,25 15,40" fill="url(#arrowGradient1)" filter="url(#glow1)" />
                          <line x1="10" y1="25" x2="35" y2="25" stroke="url(#arrowGradient1)" strokeWidth="2" filter="url(#glow1)" strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/30 to-purple-600/30 rounded-lg blur-lg animate-pulse"></div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 animate-fade-in-up">Traditional Encryption</h3>
                      <p className="text-gray-700 animate-fade-in-up">Imagine a vault with a combination lock that takes a normal person 10,000 years to guess all possible combinations. You feel safe.</p>
                    </div>
                  </div>

                  <div className={`flex gap-4 transition-all duration-1000 ${analogyRef.isVisible ? 'opacity-100 animate-slide-in-right' : 'opacity-0'}`} style={{ transitionDelay: analogyRef.isVisible ? '0.3s' : undefined }}>
                    <div className="flex-shrink-0">
                      <div className="relative w-12 h-12">
                        <svg className="w-full h-full" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <linearGradient id="arrowGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" style={{ stopColor: '#A855F7', stopOpacity: 0.4 }} />
                              <stop offset="50%" style={{ stopColor: '#9333EA', stopOpacity: 1 }} />
                              <stop offset="100%" style={{ stopColor: '#7E22CE', stopOpacity: 0.4 }} />
                            </linearGradient>
                            <filter id="glow2">
                              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                              <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                              </feMerge>
                            </filter>
                          </defs>
                          <polygon points="15,10 35,25 15,40" fill="url(#arrowGradient2)" filter="url(#glow2)" />
                          <line x1="10" y1="25" x2="35" y2="25" stroke="url(#arrowGradient2)" strokeWidth="2" filter="url(#glow2)" strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/30 to-purple-600/30 rounded-lg blur-lg animate-pulse"></div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 animate-fade-in-up">Along Comes Quantum</h3>
                      <p className="text-gray-700 animate-fade-in-up">Then someone invents a superhuman lockpicker that can try all 10,000 years of guesses in seconds. Your vault isn't safe anymore.</p>
                    </div>
                  </div>

                  <div className={`flex gap-4 transition-all duration-1000 ${analogyRef.isVisible ? 'opacity-100 animate-slide-in-right' : 'opacity-0'}`} style={{ transitionDelay: analogyRef.isVisible ? '0.4s' : undefined }}>
                    <div className="flex-shrink-0">
                      <div className="relative w-12 h-12">
                        <svg className="w-full h-full" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <linearGradient id="arrowGradient3" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" style={{ stopColor: '#A855F7', stopOpacity: 0.4 }} />
                              <stop offset="50%" style={{ stopColor: '#9333EA', stopOpacity: 1 }} />
                              <stop offset="100%" style={{ stopColor: '#7E22CE', stopOpacity: 0.4 }} />
                            </linearGradient>
                            <filter id="glow3">
                              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                              <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                              </feMerge>
                            </filter>
                          </defs>
                          <polygon points="15,10 35,25 15,40" fill="url(#arrowGradient3)" filter="url(#glow3)" />
                          <line x1="10" y1="25" x2="35" y2="25" stroke="url(#arrowGradient3)" strokeWidth="2" filter="url(#glow3)" strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/30 to-purple-600/30 rounded-lg blur-lg animate-pulse"></div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 animate-fade-in-up">Post-Quantum Encryption</h3>
                      <p className="text-gray-700 animate-fade-in-up">Post-quantum algorithms use math that's hard even for quantum computers. New, stronger vaults that will stay safe for decades to come.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Now - The Real Risk */}
      <section className="py-24 bg-white" ref={riskRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isMobile ? (
            <motion.h2
              className="text-5xl font-bold text-gray-900 mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              Why You Can't Wait
            </motion.h2>
          ) : (
            <h2 className={`text-5xl font-bold text-gray-900 mb-16 transition-all duration-1000 ${riskRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: riskRef.isVisible ? '0s' : undefined }}>
              Why You Can't Wait
            </h2>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {isMobile ? (
              <>
                <motion.div
                  className="bg-gradient-to-br from-cyan-50 to-blue-50 p-8 rounded-xl border border-cyan-200"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <Lock className="text-cyan-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Migration Takes Years</h3>
                  <p className="text-gray-700">
                    You can't flip a switch and change everything overnight. Most organizations need 2-3 years to complete migration across all systems, applications, and infrastructure.
                  </p>
                </motion.div>

                <motion.div
                  className="bg-gradient-to-br from-purple-50 to-indigo-50 p-8 rounded-xl border border-purple-200"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <Zap className="text-purple-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Legacy Systems Are Hidden</h3>
                  <p className="text-gray-700">
                    Most organizations don't even know where their encryption is used. Old systems, forgotten applications, and embedded devices are still running vulnerable algorithms.
                  </p>
                </motion.div>

                <motion.div
                  className="bg-gradient-to-br from-orange-50 to-red-50 p-8 rounded-xl border border-orange-200"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <Building2 className="text-orange-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Supply Chain Exposure</h3>
                  <p className="text-gray-700">
                    Your vendors, partners, and third-party services also use vulnerable encryption. One weak link in the chain puts your entire ecosystem at risk.
                  </p>
                </motion.div>

                <motion.div
                  className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-xl border border-green-200"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <AlertCircle className="text-green-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Compliance Mandates</h3>
                  <p className="text-gray-700">
                    Regulators are coming. Missing the deadline isn't just a technical issue—it's a compliance violation with serious consequences.
                  </p>
                </motion.div>
              </>
            ) : (
              <>
                <div className={`bg-gradient-to-br from-cyan-50 to-blue-50 p-8 rounded-xl border border-cyan-200 transition-all duration-1000 hover:scale-110 hover:shadow-xl ${riskRef.isVisible ? 'opacity-100 animate-scale-in' : 'opacity-0'}`} style={{ transitionDelay: riskRef.isVisible ? '0.1s' : undefined }}>
                  <Lock className="text-cyan-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Migration Takes Years</h3>
                  <p className="text-gray-700">
                    You can't flip a switch and change everything overnight. Most organizations need 2-3 years to complete migration across all systems, applications, and infrastructure.
                  </p>
                </div>

                <div className={`bg-gradient-to-br from-purple-50 to-indigo-50 p-8 rounded-xl border border-purple-200 transition-all duration-1000 hover:scale-110 hover:shadow-xl ${riskRef.isVisible ? 'opacity-100 animate-scale-in' : 'opacity-0'}`} style={{ transitionDelay: riskRef.isVisible ? '0.2s' : undefined }}>
                  <Zap className="text-purple-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Legacy Systems Are Hidden</h3>
                  <p className="text-gray-700">
                    Most organizations don't even know where their encryption is used. Old systems, forgotten applications, and embedded devices are still running vulnerable algorithms.
                  </p>
                </div>

                <div className={`bg-gradient-to-br from-orange-50 to-red-50 p-8 rounded-xl border border-orange-200 transition-all duration-1000 ${riskRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: riskRef.isVisible ? '0.3s' : undefined }}>
                  <Building2 className="text-orange-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Supply Chain Exposure</h3>
                  <p className="text-gray-700">
                    Your vendors, partners, and third-party services also use vulnerable encryption. One weak link in the chain puts your entire ecosystem at risk.
                  </p>
                </div>

                <div className={`bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-xl border border-green-200 transition-all duration-1000 ${riskRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: riskRef.isVisible ? '0.4s' : undefined }}>
                  <AlertCircle className="text-green-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Compliance Mandates</h3>
                  <p className="text-gray-700">
                    Regulators are coming. Missing the deadline isn't just a technical issue—it's a compliance violation with serious consequences.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Regulations - The Reality Check */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-purple-900 text-white" ref={regulationRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isMobile ? (
            <motion.h2
              className="text-5xl font-bold mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              The Clock is Ticking
            </motion.h2>
          ) : (
            <h2 className={`text-5xl font-bold mb-16 transition-all duration-1000 ${regulationRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: regulationRef.isVisible ? '0s' : undefined }}>
              The Clock is Ticking
            </h2>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {isMobile ? (
              <>
                <motion.div
                  className="bg-white/10 backdrop-blur-md p-8 rounded-xl border border-white/20"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="text-cyan-400 font-bold text-sm mb-2">BY 2030</div>
                  <h3 className="text-2xl font-bold mb-3">NIST Standards Ready</h3>
                  <p className="text-gray-200">
                    Post-quantum encryption standards are finalized. Organizations must begin migration NOW to meet 2030 compliance windows.
                  </p>
                </motion.div>

                <motion.div
                  className="bg-white/10 backdrop-blur-md p-8 rounded-xl border border-white/20"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="text-cyan-400 font-bold text-sm mb-2">2027-2029</div>
                  <h3 className="text-2xl font-bold mb-3">Federal Requirements</h3>
                  <p className="text-gray-200">
                    U.S. federal agencies must transition to post-quantum cryptography. This cascades to all contractors and service providers.
                  </p>
                </motion.div>

                <motion.div
                  className="bg-white/10 backdrop-blur-md p-8 rounded-xl border border-white/20"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="text-cyan-400 font-bold text-sm mb-2">2029-2030</div>
                  <h3 className="text-2xl font-bold mb-3">Industry Mandates</h3>
                  <p className="text-gray-200">
                    Financial institutions, healthcare, and critical infrastructure face regulatory deadlines. Compliance is non-negotiable.
                  </p>
                </motion.div>
              </>
            ) : (
              <>
                <div className={`bg-white/10 backdrop-blur-md p-8 rounded-xl border border-white/20 transition-all duration-1000 ${regulationRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: regulationRef.isVisible ? '0.1s' : undefined }}>
                  <div className="text-cyan-400 font-bold text-sm mb-2">BY 2030</div>
                  <h3 className="text-2xl font-bold mb-3">NIST Standards Ready</h3>
                  <p className="text-gray-200">
                    Post-quantum encryption standards are finalized. Organizations must begin migration NOW to meet 2030 compliance windows.
                  </p>
                </div>

                <div className={`bg-white/10 backdrop-blur-md p-8 rounded-xl border border-white/20 transition-all duration-1000 ${regulationRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: regulationRef.isVisible ? '0.2s' : undefined }}>
                  <div className="text-cyan-400 font-bold text-sm mb-2">2027-2029</div>
                  <h3 className="text-2xl font-bold mb-3">Federal Requirements</h3>
                  <p className="text-gray-200">
                    U.S. federal agencies must transition to post-quantum cryptography. This cascades to all contractors and service providers.
                  </p>
                </div>

                <div className={`bg-white/10 backdrop-blur-md p-8 rounded-xl border border-white/20 transition-all duration-1000 ${regulationRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: regulationRef.isVisible ? '0.3s' : undefined }}>
                  <div className="text-cyan-400 font-bold text-sm mb-2">2029-2030</div>
                  <h3 className="text-2xl font-bold mb-3">Industry Mandates</h3>
                  <p className="text-gray-200">
                    Financial institutions, healthcare, and critical infrastructure face regulatory deadlines. Compliance is non-negotiable.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* What Industry Leaders Are Doing - Horizontal Timeline */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-cyan-50" ref={expertRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className={`text-5xl font-bold text-gray-900 mb-24 text-center transition-all duration-1000 ${expertRef.isVisible ? 'opacity-100 animate-slide-in-down' : 'opacity-0'}`} style={{ transitionDelay: expertRef.isVisible ? '0s' : undefined }}>
            What Industry Leaders Are Doing
          </h2>

          {/* Horizontal Timeline Container */}
          <div className="relative">
            {/* Vertical Center Line - Hidden on mobile, shown on md+ */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-cyan-500 via-blue-500 to-cyan-500" style={{ height: '100%' }}></div>

            {/* Mobile Timeline Line - Left aligned, shown only on mobile */}
            <div className="md:hidden absolute left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-cyan-500 via-blue-500 to-cyan-500" style={{ height: '100%' }}></div>

            {/* Timeline Item 1 - Gartner (Left on desktop, full width on mobile) */}
            <div className={`mb-12 md:mb-20 transition-all duration-1500 ease-out ${expertRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 md:-translate-x-20'}`} style={{ transitionDelay: expertRef.isVisible ? '0.1s' : undefined }}>
              {/* Mobile Layout */}
              <div className="flex md:hidden items-start gap-4 pl-2">
                {/* Mobile: Dot */}
                <div className="flex-shrink-0 relative w-6 h-6 mt-8">
                  <div className="absolute inset-0 rounded-full bg-blue-600 animate-pulse shadow-lg" style={{ boxShadow: '0 0 20px rgba(37, 99, 235, 0.8)' }}></div>
                  <div className="absolute inset-0 rounded-full bg-blue-500 opacity-40 animate-pulse"></div>
                </div>
                {/* Mobile: Card */}
                <div className="flex-1">
                  <div className="group relative bg-white rounded-2xl p-5 transition-all duration-300" style={{ border: '1px solid rgba(249, 115, 22, 0.2)', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)' }}>
                    <div className="relative z-10">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Gartner</h3>
                      <p className="text-sm text-gray-700 italic mb-3">"The time to prepare is NOW. Organizations must act immediately."</p>
                      <p className="text-xs text-cyan-500 font-bold mb-4">THE URGENT ACTION</p>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-start gap-2 text-xs text-gray-700">
                          <CheckCircle size={14} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                          <span><strong>Build a cryptographic inventory</strong> to understand encryption usage</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-gray-700">
                          <CheckCircle size={14} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                          <span><strong>Develop governance policies</strong> for controlled migration</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-gray-700">
                          <CheckCircle size={14} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                          <span><strong>Engage with vendors</strong> on their migration timelines</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-gray-700">
                          <CheckCircle size={14} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                          <span><strong>Implement crypto-agility</strong> for rapid standards adaptation</span>
                        </li>
                      </ul>
                      <div className="flex justify-center">
                        <a href="https://www.gartner.com/en/articles/post-quantum-cryptography?utm_source=chatgpt.com" target="_blank" rel="noopener noreferrer" className="inline-block px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors duration-200">
                          Explore Report
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Layout */}
              <div className="hidden md:flex items-center gap-0">
                {/* Left Card */}
                <div className="w-5/12 pr-8">
                  <div className="group relative bg-white rounded-3xl p-8 transition-all duration-300 hover:scale-105 hover:-translate-y-3" style={{ border: '1px solid rgba(249, 115, 22, 0.2)', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)' }} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 30px rgba(249, 115, 22, 0.6), 0 0 60px rgba(249, 115, 22, 0.4), 0 10px 30px rgba(0, 0, 0, 0.08)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.08)'; }}>
                    <div className="relative z-10">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">Gartner</h3>
                    <p className="text-base text-gray-700 italic mb-4">
                      "The time to prepare is NOW. Organizations must act immediately."
                    </p>
                    <p className="text-sm text-cyan-500 font-bold mb-5">THE URGENT ACTION</p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start gap-3 text-sm text-gray-700">
                        <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Build a cryptographic inventory</strong> to understand encryption usage</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-gray-700">
                        <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Develop governance policies</strong> for controlled migration</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-gray-700">
                        <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Engage with vendors</strong> on their migration timelines</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-gray-700">
                        <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Implement crypto-agility</strong> for rapid standards adaptation</span>
                      </li>
                    </ul>
                    <div className="flex justify-center">
                      <a href="https://www.gartner.com/en/articles/post-quantum-cryptography?utm_source=chatgpt.com" target="_blank" rel="noopener noreferrer" className="inline-block px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors duration-200">
                        Explore Report
                      </a>
                    </div>
                    </div>
                  </div>
                </div>

                {/* Center: Dot */}
                <div className="w-2/12 flex justify-center">
                  <div className="relative flex flex-col items-center">
                    {/* Blue glowing dot with pulse */}
                    <div className="relative w-6 h-6">
                      <div className="absolute inset-0 rounded-full bg-blue-600 animate-pulse shadow-lg" style={{ boxShadow: '0 0 20px rgba(37, 99, 235, 0.8)' }}></div>
                      <div className="absolute inset-0 rounded-full bg-blue-500 opacity-40 animate-pulse"></div>
                    </div>
                  </div>
                </div>

                {/* Right empty space */}
                <div className="w-5/12"></div>
              </div>
            </div>

            {/* Timeline Item 2 - NIST (Right on desktop, full width on mobile) */}
            <div className={`mb-12 md:mb-20 transition-all duration-1500 ease-out ${expertRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 md:translate-x-20'}`} style={{ transitionDelay: expertRef.isVisible ? '0.5s' : undefined }}>
              {/* Mobile Layout */}
              <div className="flex md:hidden items-start gap-4 pl-2">
                {/* Mobile: Dot */}
                <div className="flex-shrink-0 relative w-6 h-6 mt-8">
                  <div className="absolute inset-0 rounded-full bg-blue-600 animate-pulse shadow-lg" style={{ boxShadow: '0 0 20px rgba(37, 99, 235, 0.8)' }}></div>
                  <div className="absolute inset-0 rounded-full bg-blue-500 opacity-40 animate-pulse"></div>
                </div>
                {/* Mobile: Card */}
                <div className="flex-1">
                  <div className="group relative bg-white rounded-2xl p-5 transition-all duration-300" style={{ border: '1px solid rgba(6, 182, 212, 0.2)', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)' }}>
                    <div className="relative z-10">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">NIST</h3>
                      <p className="text-sm text-gray-700 italic mb-3">"The transition is not optional. We have officially set the timeline to deprecate current standards by 2030."</p>
                      <p className="text-xs text-cyan-500 font-bold mb-4">THE TECHNICAL STANDARD • 2030</p>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-start gap-2 text-xs text-gray-700">
                          <CheckCircle size={14} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                          <span><strong>Prioritize immediate action</strong> to prevent "Harvest Now, Decrypt Later" attacks</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-gray-700">
                          <CheckCircle size={14} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                          <span><strong>Adopt finalized standards</strong> using FIPS 203, 204, and 205</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-gray-700">
                          <CheckCircle size={14} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                          <span><strong>Plan for deprecation</strong> of RSA and ECC by 2030</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-gray-700">
                          <CheckCircle size={14} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                          <span><strong>Ensure compliance</strong> with FIPS 140-3 requirements</span>
                        </li>
                      </ul>
                      <div className="flex justify-center">
                        <a href="https://csrc.nist.gov/projects/post-quantum-cryptography" target="_blank" rel="noopener noreferrer" className="inline-block px-3 py-1.5 bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-semibold rounded-lg transition-colors duration-200">
                          View Standards
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Layout */}
              <div className="hidden md:flex items-center gap-0">
                {/* Left empty space */}
                <div className="w-5/12"></div>

                {/* Center: Dot */}
                <div className="w-2/12 flex justify-center">
                  <div className="relative flex flex-col items-center">
                    {/* Blue glowing dot with pulse */}
                    <div className="relative w-6 h-6">
                      <div className="absolute inset-0 rounded-full bg-blue-600 animate-pulse shadow-lg" style={{ boxShadow: '0 0 20px rgba(37, 99, 235, 0.8)' }}></div>
                      <div className="absolute inset-0 rounded-full bg-blue-500 opacity-40 animate-pulse"></div>
                    </div>
                  </div>
                </div>

                {/* Right Card */}
                <div className="w-5/12 pl-8">
                  <div className="group relative bg-white rounded-3xl p-8 transition-all duration-300 hover:scale-105 hover:-translate-y-3" style={{ border: '1px solid rgba(6, 182, 212, 0.2)', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)' }} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 30px rgba(6, 182, 212, 0.6), 0 0 60px rgba(6, 182, 212, 0.4), 0 10px 30px rgba(0, 0, 0, 0.08)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.08)'; }}>
                    <div className="relative z-10">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">NIST</h3>
                    <p className="text-base text-gray-700 italic mb-4">
                      "The transition is not optional. We have officially set the timeline to deprecate current standards by 2030."
                    </p>
                    <p className="text-sm text-cyan-500 font-bold mb-5">THE TECHNICAL STANDARD • 2030</p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start gap-3 text-sm text-gray-700">
                        <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Prioritize immediate action</strong> to prevent "Harvest Now, Decrypt Later" attacks</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-gray-700">
                        <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Adopt finalized standards</strong> using FIPS 203, 204, and 205</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-gray-700">
                        <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Plan for deprecation</strong> of RSA and ECC by 2030</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-gray-700">
                        <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Ensure compliance</strong> with FIPS 140-3 requirements</span>
                      </li>
                    </ul>
                    <div className="flex justify-center">
                      <a href="https://csrc.nist.gov/projects/post-quantum-cryptography" target="_blank" rel="noopener noreferrer" className="inline-block px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold rounded-lg transition-colors duration-200">
                        View Standards
                      </a>
                    </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline Item 3 - White House (Left on desktop, full width on mobile) */}
            <div className={`mb-12 md:mb-20 transition-all duration-1500 ease-out ${expertRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 md:-translate-x-20'}`} style={{ transitionDelay: expertRef.isVisible ? '0.9s' : undefined }}>
              {/* Mobile Layout */}
              <div className="flex md:hidden items-start gap-4 pl-2">
                {/* Mobile: Dot */}
                <div className="flex-shrink-0 relative w-6 h-6 mt-8">
                  <div className="absolute inset-0 rounded-full bg-blue-600 animate-pulse shadow-lg" style={{ boxShadow: '0 0 20px rgba(37, 99, 235, 0.8)' }}></div>
                  <div className="absolute inset-0 rounded-full bg-blue-500 opacity-40 animate-pulse"></div>
                </div>
                {/* Mobile: Card */}
                <div className="flex-1">
                  <div className="group relative bg-white rounded-2xl p-5 transition-all duration-300" style={{ border: '1px solid rgba(34, 197, 94, 0.2)', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)' }}>
                    <div className="relative z-10">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">White House</h3>
                      <p className="text-sm text-gray-700 italic mb-3">NSM-10: "The U.S. must prioritize quantum-resistant cryptography transition by 2035."</p>
                      <p className="text-xs text-cyan-500 font-bold mb-4">THE FEDERAL MANDATE • 2035</p>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-start gap-2 text-xs text-gray-700">
                          <CheckCircle size={14} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                          <span><strong>Acknowledge the threat</strong> of adversarial "Harvest Now, Decrypt Later" strategies</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-gray-700">
                          <CheckCircle size={14} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                          <span><strong>Meet the deadline</strong> for full migration of National Security Systems by 2035</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-gray-700">
                          <CheckCircle size={14} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                          <span><strong>Follow phased enforcement</strong> starting with software/firmware updates by 2030</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-gray-700">
                          <CheckCircle size={14} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                          <span><strong>Modernize infrastructure</strong> for CNSA 2.0 algorithm suite</span>
                        </li>
                      </ul>
                      <div className="flex justify-center">
                        <a href="https://www.whitehouse.gov/presidential-actions/2025/06/sustaining-select-efforts-to-strengthen-the-nations-cybersecurity-and-amending-executive-order-13694-and-executive-order-14144/#:~:text=Section%201.,3(c)%2C%20respectively;" target="_blank" rel="noopener noreferrer" className="inline-block px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold rounded-lg transition-colors duration-200">
                          Read Mandate
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Layout */}
              <div className="hidden md:flex items-center gap-0">
                {/* Left Card */}
                <div className="w-5/12 pr-8">
                  <div className="group relative bg-white rounded-3xl p-8 transition-all duration-300 hover:scale-105 hover:-translate-y-3" style={{ border: '1px solid rgba(34, 197, 94, 0.2)', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)' }} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 30px rgba(34, 197, 94, 0.6), 0 0 60px rgba(34, 197, 94, 0.4), 0 10px 30px rgba(0, 0, 0, 0.08)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.08)'; }}>
                    <div className="relative z-10">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">White House</h3>
                    <p className="text-base text-gray-700 italic mb-4">
                      NSM-10: "The U.S. must prioritize quantum-resistant cryptography transition by 2035."
                    </p>
                    <p className="text-sm text-cyan-500 font-bold mb-5">THE FEDERAL MANDATE • 2035</p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start gap-3 text-sm text-gray-700">
                        <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Acknowledge the threat</strong> of adversarial "Harvest Now, Decrypt Later" strategies</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-gray-700">
                        <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Meet the deadline</strong> for full migration of National Security Systems by 2035</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-gray-700">
                        <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Follow phased enforcement</strong> starting with software/firmware updates by 2030</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-gray-700">
                        <CheckCircle size={18} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Modernize infrastructure</strong> for CNSA 2.0 algorithm suite</span>
                      </li>
                    </ul>
                    <div className="flex justify-center">
                      <a href="https://www.whitehouse.gov/presidential-actions/2025/06/sustaining-select-efforts-to-strengthen-the-nations-cybersecurity-and-amending-executive-order-13694-and-executive-order-14144/#:~:text=Section%201.,3(c)%2C%20respectively;" target="_blank" rel="noopener noreferrer" className="inline-block px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded-lg transition-colors duration-200">
                        Read Mandate
                      </a>
                    </div>
                    </div>
                  </div>
                </div>

                {/* Center: Dot */}
                <div className="w-2/12 flex justify-center">
                  <div className="relative flex flex-col items-center">
                    {/* Blue glowing dot with pulse */}
                    <div className="relative w-6 h-6">
                      <div className="absolute inset-0 rounded-full bg-blue-600 animate-pulse shadow-lg" style={{ boxShadow: '0 0 20px rgba(37, 99, 235, 0.8)' }}></div>
                      <div className="absolute inset-0 rounded-full bg-blue-500 opacity-40 animate-pulse"></div>
                    </div>
                  </div>
                </div>

                {/* Right empty space */}
                <div className="w-5/12"></div>
              </div>
            </div>
          </div>

          <div className={`bg-white border border-gray-300 p-10 rounded-lg mt-16 transition-all duration-1500 ease-out shadow-md ${expertRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{ transitionDelay: expertRef.isVisible ? '1.2s' : undefined }}>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">The Bottom Line</h3>
            <p className="text-lg text-gray-700 leading-relaxed">
              Early movers gain competitive advantage. They avoid rushed decisions, secure their data, and position themselves as responsible stewards of customer information. Organizations that wait until the last minute will face chaos, cost overruns, and compliance violations.
            </p>
          </div>
        </div>
      </section>

      {/* What Organizations Should Do */}
      <section className="py-24 bg-gradient-to-br from-cyan-50 to-blue-50" ref={actionRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isMobile ? (
            <motion.h2
              className="text-5xl font-bold text-gray-900 mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              Your Action Plan
            </motion.h2>
          ) : (
            <h2 className={`text-5xl font-bold text-gray-900 mb-16 transition-all duration-1000 ${actionRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: actionRef.isVisible ? '0s' : undefined }}>
              Your Action Plan
            </h2>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { num: '1', title: 'Assess', desc: 'Understand your current cryptographic exposure' },
              { num: '2', title: 'Plan', desc: 'Develop a migration roadmap with timelines' },
              { num: '3', title: 'Pilot', desc: 'Test post-quantum algorithms on critical systems' },
              { num: '4', title: 'Deploy', desc: 'Roll out across infrastructure systematically' }
            ].map((step, idx) => (
              isMobile ? (
                <motion.div
                  key={idx}
                  className="bg-white p-8 rounded-xl border-2 border-cyan-200 shadow-lg"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: idx * 0.1, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center text-xl font-bold mb-4 animate-pulse">
                    {step.num}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-700">{step.desc}</p>
                </motion.div>
              ) : (
                <div
                  key={idx}
                  className={`bg-white p-8 rounded-xl border-2 border-cyan-200 shadow-lg transition-all duration-1000 hover:scale-110 hover:shadow-2xl ${actionRef.isVisible ? 'opacity-100 animate-zoom-in' : 'opacity-0'}`}
                  style={{ transitionDelay: actionRef.isVisible ? `${0.1 + idx * 0.15}s` : undefined }}
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center text-xl font-bold mb-4 animate-pulse">
                    {step.num}
                  </div>
                  <h3 className={`text-xl font-bold text-gray-900 mb-2 transition-all duration-1000 ${actionRef.isVisible ? 'opacity-100 animate-slide-in-down' : 'opacity-0'}`}>{step.title}</h3>
                  <p className={`text-gray-700 transition-all duration-1000 ${actionRef.isVisible ? 'opacity-100 animate-fade-in-up' : 'opacity-0'}`}>{step.desc}</p>
                </div>
              )
            ))}
          </div>

          {isMobile ? (
            <motion.div
              className="mt-16 bg-white p-8 rounded-xl border border-cyan-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              <p className="text-gray-700 text-lg leading-relaxed">
                <strong>What You Need to Know:</strong> Waiting is the most expensive option. Organizations that start their migration today will save time, money, and avoid compliance violations. The question isn't whether you need to migrate—it's whether you'll do it on your timeline or be forced to do it on someone else's.
              </p>
            </motion.div>
          ) : (
            <div className={`mt-16 bg-white p-8 rounded-xl border border-cyan-300 transition-all duration-1000 ${actionRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: actionRef.isVisible ? '0.5s' : undefined }}>
              <p className="text-gray-700 text-lg leading-relaxed">
                <strong>What You Need to Know:</strong> Waiting is the most expensive option. Organizations that start their migration today will save time, money, and avoid compliance violations. The question isn't whether you need to migrate—it's whether you'll do it on your timeline or be forced to do it on someone else's.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Coming Soon - Our Solution */}
      <section className="py-24 bg-gradient-to-br from-purple-900 to-indigo-900 text-white" ref={comingSoonRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              {isMobile ? (
                <>
                  <motion.div
                    className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-cyan-400/20 rounded-full border border-cyan-400/40"
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                    <span className="text-sm font-semibold text-cyan-300">Coming Soon</span>
                  </motion.div>

                  <motion.h2
                    className="text-4xl md:text-5xl font-bold mb-6 leading-tight"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    We're Building Your Migration Solution
                  </motion.h2>

                  <motion.p
                    className="text-xl text-gray-200 mb-8 leading-relaxed"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    Our AI-powered PQC Migration tool will help you navigate this transition with confidence. Automated discovery, intelligent planning, and guided implementation—all designed to make your migration smooth and compliant.
                  </motion.p>

                  <motion.div
                    className="space-y-4 mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <div className="flex items-start gap-4">
                      <CheckCircle className="text-green-400 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <h4 className="font-semibold mb-1">Automated Cryptographic Discovery</h4>
                        <p className="text-gray-300">Find every instance of encryption in your environment</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <CheckCircle className="text-green-400 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <h4 className="font-semibold mb-1">AI-Powered Migration Planning</h4>
                        <p className="text-gray-300">Risk-based prioritization and compliance-aligned strategy</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <CheckCircle className="text-green-400 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <h4 className="font-semibold mb-1">Guided Implementation</h4>
                        <p className="text-gray-300">Step-by-step support through every phase of migration</p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 text-gray-900 rounded-full font-bold hover:bg-cyan-400 transition-all">
                      Get Early Access & Assessment
                      <span>→</span>
                    </Link>
                  </motion.div>
                </>
              ) : (
                <>
                  <div className={`inline-flex items-center gap-2 mb-6 px-4 py-2 bg-cyan-400/20 rounded-full border border-cyan-400/40 transition-all duration-1000 ${comingSoonRef.isVisible ? 'opacity-100 animate-slide-in-down' : 'opacity-0'}`}>
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                    <span className="text-sm font-semibold text-cyan-300">Coming Soon</span>
                  </div>

                  <h2 className={`text-5xl font-bold mb-6 leading-tight transition-all duration-1000 ${comingSoonRef.isVisible ? 'opacity-100 animate-slide-in-left' : 'opacity-0'}`} style={{ transitionDelay: comingSoonRef.isVisible ? '0.1s' : undefined }}>
                    We're Building Your Migration Solution
                  </h2>

                  <p className={`text-xl text-gray-200 mb-8 leading-relaxed transition-all duration-1000 ${comingSoonRef.isVisible ? 'opacity-100 animate-fade-in-up' : 'opacity-0'}`} style={{ transitionDelay: comingSoonRef.isVisible ? '0.2s' : undefined }}>
                    Our AI-powered PQC Migration tool will help you navigate this transition with confidence. Automated discovery, intelligent planning, and guided implementation—all designed to make your migration smooth and compliant.
                  </p>

                  <div className={`space-y-4 mb-8 transition-all duration-1000 ${comingSoonRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: comingSoonRef.isVisible ? '0.3s' : undefined }}>
                    <div className="flex items-start gap-4">
                      <CheckCircle className="text-green-400 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <h4 className="font-semibold mb-1">Automated Cryptographic Discovery</h4>
                        <p className="text-gray-300">Find every instance of encryption in your environment</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <CheckCircle className="text-green-400 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <h4 className="font-semibold mb-1">AI-Powered Migration Planning</h4>
                        <p className="text-gray-300">Risk-based prioritization and compliance-aligned strategy</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <CheckCircle className="text-green-400 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <h4 className="font-semibold mb-1">Guided Implementation</h4>
                        <p className="text-gray-300">Step-by-step support through every phase of migration</p>
                      </div>
                    </div>
                  </div>

                  <Link to="/contact" className={`inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 text-gray-900 rounded-full font-bold hover:bg-cyan-400 transition-all duration-1000 hover:gap-3 ${comingSoonRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: comingSoonRef.isVisible ? '0.4s' : undefined }}>
                    Get Early Access & Assessment
                    <span>→</span>
                  </Link>
                </>
              )}
            </div>

            {isMobile ? (
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                <img
                  src="/PQC_migration_roadmap.jpg"
                  alt="PQC Migration Roadmap"
                  className="w-full rounded-3xl shadow-2xl"
                />
              </motion.div>
            ) : (
              <div className={`relative transition-all duration-1000 ${comingSoonRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: comingSoonRef.isVisible ? '0.2s' : undefined }}>
                <img
                  src="/PQC_migration_roadmap.jpg"
                  alt="PQC Migration Roadmap"
                  className="w-full rounded-3xl shadow-2xl"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          {isMobile ? (
            <>
              <motion.h2
                className="text-4xl font-bold mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                Don't Wait for Quantum Computers to Arrive
              </motion.h2>
              <motion.p
                className="text-xl mb-8 opacity-90"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                Start your migration assessment today. Let's build your quantum-safe future together.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                <Link to="/contact" className="px-8 py-4 bg-white text-blue-600 rounded-full font-bold hover:bg-gray-100 transition-all inline-block">
                  Talk to Us Today
                </Link>
              </motion.div>
            </>
          ) : (
            <>
              <h2 className="text-4xl font-bold mb-4">Don't Wait for Quantum Computers to Arrive</h2>
              <p className="text-xl mb-8 opacity-90">Start your migration assessment today. Let's build your quantum-safe future together.</p>
              <Link to="/contact" className="px-8 py-4 bg-white text-blue-600 rounded-full font-bold hover:bg-gray-100 transition-all inline-block hover:scale-105">
                Talk to Us Today
              </Link>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default QSM;
